import React, { PureComponent } from "react";
import { connect } from "react-redux";
import styled from "styled-components/macro";
import { withTranslation, WithTranslation } from "react-i18next";
import {
  EXPAND_COLUMN_WIDTH,
  SIDELIST_COLUMN_BACKGROUND,
  SIDELIST_COLUMN_BACKGROUND_HOVER,
  SIDELIST_COLUMN_BACKGROUND_ACTIVE,
  BORDER_COLOR,
} from "../../helpers/Const";
import { translatePropertyDefName } from "../../../backend/helpers/translationHelpers";
import { iRootState } from "src/rematch/store";
import IMFilesPropertyDefinition from "src/backend/interfaces/mfiles/IMFilesPropertyDefinition";

interface IColumnsRootProps {
  width: number;
}

const ColumnsRoot = styled.div`
  width: ${(props: IColumnsRootProps) => props.width}px;
  border-bottom: solid 1px ${BORDER_COLOR};
  position: sticky;
  top: 0px;
  z-index: 20;
`;

const ColumnDummy = styled.div`
  height: 100%;
  width: 300px;
  background: ${SIDELIST_COLUMN_BACKGROUND};
`;

interface IColumnsContainerProps {
  height: number;
}

const ColumnsContainer = styled.div`
height: ${(props: IColumnsContainerProps) => props.height}px;
  display: flex;
  overflow-x: hidden;
`;

const ColumnResizer = styled.div`
  cursor: col-resize;
  position: absolute;
  width: 10px;
  height: 100%;
  right: 0;
`;

const StyledColumnHeaderContainer = styled.div`
  flex: 0 0 auto;
  position: relative;
  border-right: solid 1px ${BORDER_COLOR};
  background: ${SIDELIST_COLUMN_BACKGROUND};
  overflow: hidden;
  &:hover {
    background: ${SIDELIST_COLUMN_BACKGROUND_HOVER};
  }
  &:active {
    background: ${SIDELIST_COLUMN_BACKGROUND_ACTIVE};
  }
  :nth-last-child(2) {
    border-right: 0;
  }
`;

interface IColumnHeaderTextContainerProps {
  isExpander?: boolean;
}

const ColumnHeaderTextContainer = styled.div`
  font-size: ${(props: IColumnHeaderTextContainerProps) => (props.isExpander ? "22px" : "14px")};
  padding: ${(props: IColumnHeaderTextContainerProps) => (props.isExpander ? "0px" : "3px 5px")};
  display: ${(props: IColumnHeaderTextContainerProps) => (props.isExpander ? "flex" : "block")};
  text-align: ${(props: IColumnHeaderTextContainerProps) => (props.isExpander ? "center" : "left")};
  justify-content: ${(props: IColumnHeaderTextContainerProps) => (props.isExpander ? "center" : "normal")};
  align-items: center;
  font-weight: bold;
  line-height: 20px;
  text-transform: uppercase;
  height: 100%;
  width: 100%;
  color: #525252;
  white-space: pre-line;
`;

interface IColumnHeaderProps {
  isExpander?: boolean;
  onColumnResize?: (columnIndex: number, value: number) => void;
  onClickFunc: (columnName: string) => void;
  columnText: string;
  sortOrder: { columnName: string, isAscending: boolean }[];
  columnName: string;
  columnIndex?: number;
  width?: number;
}

class ColumnHeader extends PureComponent<IColumnHeaderProps> {
  draggingPosition = 0;
  columnResizeUnderway = false;

  onMouseDown = (e) => {
    if (e.button === 0) {
      this.draggingPosition = e.clientX;
      this.columnResizeUnderway = true;
      document.addEventListener("mousemove", this.onMouseMove);
      document.addEventListener("mouseup", this.onMouseUp);
    }
  };

  onMouseMove = (e) => {
    const delta = e.clientX - this.draggingPosition;
    this.draggingPosition = e.clientX;
    this.props.onColumnResize(this.props.columnIndex, delta);
  };

  onMouseUp = () => {
    // The setTimeout function call are made so that the mouseup event is handled after the click event.
    // The click event handler checks if a drag is underway and if so, does nothing.
    setTimeout((_e) => {
      this.columnResizeUnderway = false;
      document.removeEventListener("mousemove", this.onMouseMove);
      document.removeEventListener("mouseup", this.onMouseUp);
    });
  };

  applySort = () => {
    if (!this.columnResizeUnderway) {
      this.props.onClickFunc(this.props.columnName);
    }
  };

  render() {
    let innerText;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const expandColumnIE11ExtraWidth = (window as any).msCrypto ? -1 : 0; //IE11 check
    if (this.props.isExpander) {
      innerText = (
        <div>
          +<br />-
        </div>
      );
    } else {
      innerText = this.props.columnText;
      if (this.props.sortOrder.length > 0 && this.props.sortOrder[0].columnName === this.props.columnName) {
        innerText += this.props.sortOrder[0].isAscending ? " ▼" : " ▲";
      }
    }
    return (
      <StyledColumnHeaderContainer
        style={{
          flexBasis: this.props.isExpander ? EXPAND_COLUMN_WIDTH + expandColumnIE11ExtraWidth : this.props.width,
          justifyContent: this.props.isExpander ? "center" : undefined
        }}
        onClick={() => this.props.isExpander ? this.props.onClickFunc(this.props.columnName) : this.applySort()}
      >
        {!this.props.isExpander ? <ColumnResizer onMouseDown={this.onMouseDown} /> : undefined}
        <ColumnHeaderTextContainer isExpander={this.props.isExpander}>{innerText}</ColumnHeaderTextContainer>
      </StyledColumnHeaderContainer>
    );
  }
}

interface ISideBarColumnsProps extends WithTranslation {
  getPropertyDefinition: (propertyId: number) => IMFilesPropertyDefinition;
  height: number;
  width: number;
  sortOrder: { columnName: string, isAscending: boolean }[];
  onColumnResize?: (columnIndex: number, value: number) => void;
  onAddSortColumn: (columnName: string) => void;
  onToggleExpandAll: (columnName: string) => void;
  visibleColumns: { name: string, width: number }[];
  columnPropertyMap: Map<number,number[]>;
}

class SideBarColumns extends PureComponent<ISideBarColumnsProps> {
  renderColumnText(columnIndex: number, name: string): string {
    const { t } = this.props;
    if(!name.startsWith("text")) {
      return t(`property.${name}`);
    }
    const columnNames: string[] = [];
    for(const columns of this.props.columnPropertyMap.values()) {
      const propertyId = columns[columnIndex];
      let columnName = "";
      if(propertyId !== undefined) {
        if(typeof propertyId === "string") {
          const translationKey = `property.${propertyId}`;
          columnName = t(translationKey);
        } else {
          const propertyDef = this.props.getPropertyDefinition(propertyId);
          columnName = translatePropertyDefName(propertyId, propertyDef.Name);
        }
        if(!columnNames.includes(columnName)) {
          columnNames.push(columnName);
        }
      }
    }
    return columnNames.join("\n");
  }
  renderColumns() {
    const columns = [];
    let i = 0;
    columns.push(
      <ColumnHeader
        key={-1}
        onClickFunc={this.props.onToggleExpandAll}
        isExpander={true}
        columnName="+-"
        columnText="+-"
        sortOrder={this.props.sortOrder}
      />
    );
    for (const { width, name } of this.props.visibleColumns) {
      columns.push(
        <ColumnHeader
          key={i}
          columnIndex={i}
          onClickFunc={this.props.onAddSortColumn}
          onColumnResize={this.props.onColumnResize}
          width={width}
          columnName={name}
          columnText={this.renderColumnText(i, name)}
          sortOrder={this.props.sortOrder}
        />
      );
      i++;
    }
    columns.push(<ColumnDummy key={i}></ColumnDummy>);
    return columns;
  }
  render() {
    return (
      <ColumnsRoot width={this.props.width}>
        <ColumnsContainer height={this.props.height}>{this.renderColumns()}</ColumnsContainer>
      </ColumnsRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  getPropertyDefinition: (propertyId: number) => state.common.configuration.propertyDefinitions.get(propertyId),
});

export default withTranslation()(connect(mapState, null)(SideBarColumns));
